import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Typography
} from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Select, { Option } from 'components/Common/Select';
import Input from 'components/Common/Input';
import { useCreateOrUpdateAgentAction, useGetAgentAction } from 'apis/business/agent/action/hook';
import { FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import Spinner from 'components/Common/Spinner';
import { AgentActionRequest } from 'apis/business/agent/action/types';
import { useGetOperators } from 'apis/business/setting/operator/hook';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateOauthIntegration,
  useDeleteIntegration,
  useGetAvailableIntegrations,
  useGetCalenders,
  useGetIntegrations
} from 'apis/business/setting/integrations/hook';
import Radio from 'components/Common/Radio';
import { RiDeleteBin6Line } from 'react-icons/ri';
import IntegrationForm from 'containers/Business/Setting/Integrations/Components/IntegrationForm';
import OperatorForm from 'containers/Business/Setting/Operator/Components/OperatorForm';
import * as urls from 'routes/config';
import { zodResolver } from '@hookform/resolvers/zod';
import { ActionFormSchema } from 'utils/schema/validator/ActionFormValidation';
import { IoChevronDownOutline } from 'react-icons/io5';
import Switch from 'components/Common/Switch';
import { useGetWorksheets } from 'apis/business/agent/worksheet/hook';
import { GoPlus } from 'react-icons/go';
import { useGetAgentTypeOptions } from 'apis/business/agent/agentActions/hook';
import { titleCase } from 'utils/helperFunctions';

export default function ActionForm() {
  const navigate = useNavigate();
  const { workspaceId, agentId, actionId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState<any>(false);
  const [isOperatorModalOpen, setOperatorIsModalOpen] = useState<any>(false);
  const [operatorOptions, setOperatorOptions] = useState<Option[]>([]);

  const { data: agentAction, isLoading } = useGetAgentAction();
  const { data: agentTypes, isLoading: isLoadingActionTypes } = useGetAgentTypeOptions();
  const { data: operator, isLoading: isOperatorLoading } = useGetOperators(currentPage);
  const { data: worksheets, isLoading: isWorksheetLoading } = useGetWorksheets();
  const { data: availableIntegrations } = useGetAvailableIntegrations();

  const { mutate: handleAgentAction, isPending } = useCreateOrUpdateAgentAction();
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();
  const { mutateAsync: handleCreateIntegration } = useCreateOauthIntegration();

  const { handleSubmit, control, setValue, watch, getValues } = useForm<AgentActionRequest>({
    resolver: zodResolver(ActionFormSchema),
    defaultValues: (() => {
      const tempData = localStorage.getItem('tempData');
      return tempData
        ? JSON.parse(tempData)
        : { ...agentAction, action_details: { ...agentAction?.action_details, http_method: 'get' } };
    })(),
    values: agentAction
  });

  const { data: integrations } = useGetIntegrations({
    'filter[category_eq]': watch('action_type') === 'book_appointment' ? 'calendar_management' : 'ehr_emr_system'
  });

  const actionType = watch('action_type');
  const activeIntegrationKey = filterIntegrationData[actionType];

  const combinedData = useMemo(() => {
    return (
      availableIntegrations?.data
        ?.filter((item) => [activeIntegrationKey].includes(item.category))
        .map((item) => {
          const matchingIntegration = integrations?.data?.find((integration) => integration.code === item.code);
          return {
            ...item,
            active: matchingIntegration?.active || false,
            pretty_name: matchingIntegration?.pretty_name,
            integrationId: matchingIntegration?.id || null
          };
        }) ?? []
    );
  }, [availableIntegrations, integrations, activeIntegrationKey]);

  useEffect(() => {
    if (operator?.data) {
      setOperatorOptions((prevOptions) => {
        const existingIds = new Set(prevOptions.map((option) => option.value));
        const newOptions = operator.data
          .filter((item) => !existingIds.has(item.phone_number))
          .map((item) => ({
            label: `${item.first_name} (${item.phone_number})`,
            value: item.id
          }));
        return [...prevOptions, ...newOptions];
      });
    }
  }, [operator]);

  const loadMoreOperator = () => {
    if (!isOperatorLoading && currentPage < (operator?.meta.total_pages || 1)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const selectedIntegration = watch('action_details.integration.integration_id');
  const { data: calenders, isLoading: isCalenderLoading } = useGetCalenders(Number(selectedIntegration), actionType);

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: 'action_details.headers'
  });

  const {
    fields: bodyParamsFields,
    append: bodyParamsAppend,
    remove: bodyParamsRemove
  } = useFieldArray({
    control,
    name: 'action_details.body_params'
  });

  const {
    fields: queryParamsFields,
    append: queryParamsAppend,
    remove: queryParamsRemove
  } = useFieldArray({
    control,
    name: 'action_details.query_params'
  });

  useEffect(() => {
    if (selectedIntegration !== agentAction?.action_details?.integration?.integration_id) {
      setValue('action_details.integration.calendar_id', '');
    } else {
      setValue('action_details.integration.calendar_id', agentAction?.action_details?.integration?.calendar_id);
    }
  }, [selectedIntegration, setValue, agentAction]);

  const onSubmit = (data: any) => {
    handleAgentAction(data);
  };

  const integrationOptions = combinedData.map((item) => ({
    label: (
      <Box border='1px solid #ECECEC' py={0.5} px={1} borderRadius={1} display='flex' gap={1} alignItems='center'>
        <img
          src={item.small_avatar_url}
          alt={`${item.pretty_name} icon`}
          style={{ height: '40px', width: '80px', objectFit: 'contain' }}
        />
        <Box>
          {item.pretty_name ?? item.name}
          {item.active ? (
            <Box display='flex' gap={1} alignItems='center'>
              <Typography>activated</Typography>
              <RiDeleteBin6Line
                onClick={() => deleteIntegration(item.integrationId!)}
                style={{ color: 'red', cursor: 'pointer' }}
              />
            </Box>
          ) : (
            <Typography
              color='primary'
              variant='body2'
              fontWeight={600}
              sx={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                handleActiveIntegration(item.code);
                setValue('action_details.integration.calendar_id', '');
              }}
            >
              Connect
            </Typography>
          )}
        </Box>
      </Box>
    ),
    value: item.integrationId!,
    disabled: !item.active
  }));

  const handleActiveIntegration = async (code: string) => {
    const integration = availableIntegrations?.data?.find((item: any) => item.code === code);
    if (integration?.connection_type === 'oauth') {
      localStorage.setItem('tempData', JSON.stringify(getValues()));
      handleCreateIntegration(code as any);
    } else {
      setIsModalOpen(code);
    }
  };

  const selectedWorksheet = worksheets?.data?.find((item) => item.id === watch('action_details.worksheet_id'));

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <>
      <Modal
        open={true}
        onClose={() =>
          navigate(`${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}/${urls.configure.action}`)
        }
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <Typography variant='h6'>Actions</Typography>
          <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
            <Select
              name='action_type'
              disabled={isLoadingActionTypes}
              label='Action Type'
              control={control}
              options={
                agentTypes?.data?.map((item) => ({
                  label: titleCase(item),
                  value: item
                })) || []
              }
            />
            <Typography fontSize='12px' color='secondary'>
              Select what type of action you need
            </Typography>
            <Input name='name' label='Name*' control={control} fullWidth sx={{ marginTop: 2 }} />
            <Typography fontSize='12px' color='secondary'>
              Give this integration a name
            </Typography>
            {watch('action_type') === 'transfer_call' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Box mt={2}>
                  <Select
                    label='Target Number *'
                    name='action_details.target_id'
                    isLoading={isOperatorLoading}
                    control={control}
                    options={operatorOptions}
                    onScrollEnd={loadMoreOperator}
                    handleAddNewOption={() => setOperatorIsModalOpen(true)}
                  />
                </Box>
              </>
            )}
            {watch('action_type') === 'send_sms' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Input
                  name='action_details.message_content'
                  label='Message Content'
                  control={control}
                  fullWidth
                  multiline
                  minRows={4}
                  sx={{ marginTop: 2 }}
                />
              </>
            )}
            {watch('action_type') === 'book_appointment' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Box mt={2}>
                  <Typography mb={2} color='secondary' variant='body1' fontSize={16} fontWeight={600}>
                    Select Integration
                  </Typography>
                  <Radio
                    name='action_details.integration.integration_id'
                    control={control}
                    label=''
                    options={integrationOptions ?? []}
                  />
                </Box>
                <Box mt={2}>
                  <Select
                    name='action_details.integration.calendar_id'
                    label='Select Calendar'
                    isLoading={isCalenderLoading}
                    control={control}
                    options={calenders ?? []}
                  />
                </Box>
                <Box mt={2}>
                  <Select
                    name='action_details.integration.booking_slot'
                    label='Booking Slot'
                    control={control}
                    options={[
                      { value: '30', label: '30 Minutes' },
                      { value: '45', label: '45 Minutes' },
                      { value: '60', label: '60 Minutes' },
                      { value: '120', label: '120 Minutes' }
                    ]}
                  />
                </Box>
              </>
            )}
            {watch('action_type') === 'ehr_operations' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Box mt={2}>
                  <Typography mb={2} color='secondary' variant='body1' fontSize={16} fontWeight={600}>
                    Select Integration
                  </Typography>
                  <Radio
                    name='action_details.integration.integration_id'
                    control={control}
                    label=''
                    options={integrationOptions ?? []}
                  />
                </Box>
              </>
            )}

            {watch('action_type') === 'custom_api' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Input
                  name='action_details.exposed_parameters'
                  label='Exposed Parameters'
                  placeholder='eg: order_status, dispatched_at'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                {!window.location.href.includes('nexline') && (
                  <Box display='flex' gap={1} alignItems='center'>
                    <Switch name='action_details.is_contact_authentication_required' control={control} />
                    <Typography fontSize='16px'>Is Contact Authentication Required?</Typography>
                  </Box>
                )}
                <Input
                  name='action_details.base_url'
                  label='Base Url'
                  control={control}
                  sx={{
                    marginTop: 2,
                    '.MuiFormControl-root': {
                      borderRight: '1px solid #cecece'
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment
                          position='start'
                          sx={{
                            '.MuiInputBase-root': {
                              marginTop: '10px',
                              marginRight: '10px'
                            }
                          }}
                        >
                          <Select
                            control={control}
                            name='action_details.http_method'
                            label=''
                            options={[
                              { label: 'GET', value: 'get', selected: true },
                              { label: 'POST', value: 'post' },
                              { label: 'PUT', value: 'put' }
                            ]}
                            defaultValue='get'
                            variant='standard'
                            disableUnderline
                            sx={{
                              minWidth: 80
                            }}
                          />
                        </InputAdornment>
                      )
                    }
                  }}
                  fullWidth
                />
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Headers
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {headerFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.headers[${index}].field_name`}
                          label='Field name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.headers[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.headers[${index}].value`}
                          label='Value'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => headerRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => headerAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Body Params
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {bodyParamsFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.body_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.body_params[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => bodyParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => bodyParamsAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Query Params
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {queryParamsFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.query_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.query_params[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => queryParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => queryParamsAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
            {watch('action_type') === 'contact_authentication' && (
              <>
                <Typography mt={2} fontWeight={500} fontSize={16}>
                  Contact Will Receive OTP Via
                </Typography>
                <Radio
                  name='action_details.verification_type'
                  control={control}
                  label=''
                  options={[
                    { value: 'email', label: 'Email' },
                    { value: 'phone', label: 'Phone' }
                  ]}
                />
                <Typography mt={2} fontWeight={500} fontSize={16}>
                  Set Up the API to Fetch Data for Contact Verification
                </Typography>
                <Input
                  name='action_details.base_url'
                  label='Base Url'
                  control={control}
                  sx={{
                    marginTop: 2,
                    '.MuiFormControl-root': {
                      borderRight: '1px solid #cecece'
                    }
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment
                          position='start'
                          sx={{
                            '.MuiInputBase-root': {
                              marginTop: '10px',
                              marginRight: '10px'
                            }
                          }}
                        >
                          <Select
                            control={control}
                            name='action_details.http_method'
                            label=''
                            options={[
                              { label: 'GET', value: 'get', selected: true },
                              { label: 'POST', value: 'post' },
                              { label: 'PUT', value: 'put' }
                            ]}
                            defaultValue='get'
                            variant='standard'
                            disableUnderline
                            sx={{
                              minWidth: 80
                            }}
                          />
                        </InputAdornment>
                      )
                    }
                  }}
                  fullWidth
                />
                <Accordion
                  defaultExpanded
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Header
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        mb: 3
                      }}
                    >
                      <Input
                        name='action_details.headers[0].field_name'
                        label='Field Name'
                        control={control}
                        sx={{ flex: 2 }}
                        fullWidth
                      />
                      <Input
                        name='action_details.headers[0].description'
                        label='Description'
                        control={control}
                        sx={{ flex: 2 }}
                        fullWidth
                      />
                      <Input
                        name='action_details.headers[0].value'
                        label='Value'
                        control={control}
                        sx={{ flex: 2 }}
                        fullWidth
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Query Params
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {queryParamsFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`action_details.query_params[${index}].field_name`}
                          label='Field Name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`action_details.query_params[${index}].description`}
                          label='Description'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => queryParamsRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => queryParamsAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </>
            )}
            {watch('action_type') === 'write_to_worksheet' && (
              <>
                <Input
                  name='action_details.trigger'
                  label='Trigger'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Input
                  name='action_details.agent_response'
                  label='Agent Response'
                  control={control}
                  sx={{ marginTop: 2 }}
                  fullWidth
                />
                <Box mt={2}>
                  <Select
                    label='Worksheet'
                    name='action_details.worksheet_id'
                    isLoading={isWorksheetLoading}
                    control={control}
                    options={worksheets?.data?.map((item) => ({ value: item.id, label: item.name })) ?? []}
                  />
                </Box>
                {watch('action_details.worksheet_id') && (
                  <>
                    <Typography mt={2} variant='subtitle1'>
                      Data Collect For
                    </Typography>
                    <Box display='flex' flexWrap='wrap' flexDirection='row'>
                      {selectedWorksheet?.columns?.length ? (
                        selectedWorksheet?.columns.map((column) => (
                          <Box key={column.id}>
                            <Controller
                              name='action_details.columns'
                              control={control}
                              defaultValue={[]}
                              render={({ field }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      value={column.name}
                                      checked={Array.isArray(field.value) && field.value.includes(column.name)}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (e.target.checked) {
                                          field.onChange([...(field.value || []), value]);
                                        } else {
                                          field.onChange((field.value || []).filter((v) => v !== value));
                                        }
                                      }}
                                    />
                                  }
                                  label={column.name}
                                />
                              )}
                            />
                          </Box>
                        ))
                      ) : (
                        <Typography variant='body2' color='textSecondary'>
                          No columns are added
                        </Typography>
                      )}
                      <Button
                        sx={{ textWrap: 'nowrap', minWidth: 'unset', background: 'white' }}
                        color='secondary'
                        onClick={() =>
                          navigate(
                            `add-column?worksheetId=${selectedWorksheet?.id}&position=${selectedWorksheet?.columns?.length ?? 0 + 1}`
                          )
                        }
                        variant='outlined'
                        startIcon={<GoPlus />}
                      >
                        Add Column
                      </Button>
                    </Box>
                  </>
                )}
                <Typography mt={2} variant='subtitle1'>
                  Notify Me Via
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Controller
                    name='action_details.notify_via'
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => {
                      const value = field.value || [];
                      return (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value='email'
                                checked={value.includes('email')}
                                onChange={(e) => {
                                  const checkboxValue = e.target.value;
                                  if (e.target.checked) {
                                    field.onChange([...value, checkboxValue]);
                                  } else {
                                    field.onChange(value.filter((v) => v !== checkboxValue));
                                  }
                                }}
                              />
                            }
                            label='Email'
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                value='sms'
                                checked={value.includes('sms')}
                                onChange={(e) => {
                                  const checkboxValue = e.target.value;
                                  if (e.target.checked) {
                                    field.onChange([...value, checkboxValue]); // Add value to array
                                  } else {
                                    field.onChange(value.filter((v) => v !== checkboxValue)); // Remove value from array
                                  }
                                }}
                              />
                            }
                            label='SMS/Text Message'
                          />
                        </>
                      );
                    }}
                  />
                </Box>

                <Box mt={2}>
                  <Select
                    label='Operator'
                    name='action_details.target_id'
                    isLoading={isOperatorLoading}
                    control={control}
                    options={operatorOptions}
                    onScrollEnd={loadMoreOperator}
                    handleAddNewOption={() => setOperatorIsModalOpen(true)}
                  />
                </Box>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: '16px',
                marginTop: 2
              }}
            >
              <Button
                color='secondary'
                variant='outlined'
                sx={{ border: '1px solid #F3F3F3' }}
                onClick={() =>
                  navigate(`${urls.agentBase(workspaceId, agentId)}/${urls.home.configure}/${urls.configure.action}`)
                }
              >
                Cancel
              </Button>
              <Button type='submit' variant='contained' isPending={isPending}>
                {actionId ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <IntegrationForm open={isModalOpen} handleClose={() => setIsModalOpen(false)} />
      <OperatorForm isOpen={isOperatorModalOpen} handleClose={() => setOperatorIsModalOpen(false)} />
      <Outlet />
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  maxHeight: '90vh',
  overflow: 'auto',
  width: {
    xs: '90%',
    sm: 400,
    md: 600,
    lg: 700
  }
};

const filterIntegrationData: any = {
  ehr_operations: 'ehr_emr_system',
  book_appointment: 'calendar_management'
};
